import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { MonitorPlay, LayoutGrid, FileImage, Calendar, Users, LogOut, Grid3x3, Menu, X } from 'lucide-react';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutGrid },
    { name: 'Ekrany', path: '/screens', icon: MonitorPlay },
    { name: 'Grupy Ekranów', path: '/screen-groups', icon: Grid3x3 },
    { name: 'Treści', path: '/content', icon: FileImage },
    { name: 'Harmonogram', path: '/schedules', icon: Calendar },
  ];

  if (user?.role === 'admin') {
    navigation.push({ name: 'Użytkownicy', path: '/users', icon: Users });
  }

  return (
    <div className="min-h-screen bg-zinc-950 overflow-x-hidden">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between">
        <img 
          src="https://customer-assets.emergentagent.com/job_avebar-screens/artifacts/ok83t4x1_avepark%20logo.svg" 
          alt="AvePark" 
          className="h-10 w-auto"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-white"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className={`
          w-64 min-h-screen bg-zinc-900 border-r border-zinc-800
          fixed lg:static inset-y-0 left-0 z-40
          transform transition-transform duration-300 ease-in-out
          pt-16 lg:pt-0
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_avebar-screens/artifacts/ok83t4x1_avepark%20logo.svg" 
              alt="AvePark" 
              className="h-16 w-auto mb-8"
            />
            <nav className="space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className={`nav-item flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'active bg-blue-500/10 text-blue-400'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="absolute bottom-0 left-0 w-64 p-6 border-t border-zinc-800">
            <div className="mb-4">
              <p className="text-sm text-zinc-400">Zalogowany jako</p>
              <p className="text-white font-medium">{user?.email}</p>
              <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Wyloguj
            </Button>
          </div>
        </aside>

        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8 w-full lg:w-auto">
          {children}
        </main>
      </div>
    </div>
  );
}